/**
 * The Custom option's picker, as the Create Project drawer applies it, its groups
 * right under the Custom row (multi-stack steps 3.13 and 3.14g, DESIGN_PARTS 9.2,
 * 9.5, 11.5): the rules the server checks a create request by
 * (backend/stacks/picks.py, registry.py), so the user meets each one here before
 * the create API would refuse it (a Full stack option is picked over a Frontend
 * or a Backend only through its Switch, and while one is picked their options
 * are off), the label the server gives a project (stacks/parts.py label_of),
 * custom instructions as the platform keeps and counts them
 * (stacks/instructions.py), for the Edit project drawer too (multi-stack step
 * 3.14i), the choice the drawer holds, the tech stack fields the create request
 * sends and the drawer's width, which the list decides (multi-stack step 3.14l).
 * Users read custom instructions as Special Instructions, the owner's name for
 * them (multi-stack step 3.14e); the code and the API keep theirs. The Clone
 * drawer offers the same list with "Same as the source project" first
 * (multi-stack step 3.14h), and says whether the clone gets the source code as
 * the clone API decides it (backend/services/clone_service.py clone_stack_of).
 *
 * Every word about a group or an option comes from GET /projects/stacks: the
 * groups' and options' labels, an architecture's parent, the groups a group
 * conflicts with, an option's needs and availability, and the databases each
 * server can be picked with (pairs). No stack is named here.
 *
 * Pure functions with type-only imports, so backend/tests/test_custom_picker_rules.py
 * runs this module in Node against the server's own answers.
 */
import type {
  CustomStackPicker,
  StackCatalogue,
  StackPickerGroup,
  StackPickerOption,
  StackPreset,
} from '../services/projectStacks';

/** Group -> option id: what the create request sends as stack_picks. */
export type StackPicks = Record<string, string>;

/** The Tech Stack list's value for the Custom option, which no preset may take as
 *  its id (backend/stacks/registry.py CUSTOM_CHOICE). */
export const CUSTOM_STACK = 'custom';

/** The label of a Custom stack without picks: custom instructions alone. */
const NO_PICKS_LABEL = 'Custom';

/** Why a Custom stack can't be chosen yet, as the create API says it
 *  (backend/stacks/registry.py NOTHING_CHOSEN_MESSAGE). */
export const NOTHING_CHOSEN_MESSAGE = 'Choose at least one option, or write special instructions.';

// The groups the picker's rules name (backend/stacks/picks.py). The groups, their
// order and their relations are the owner's (DESIGN_PARTS 3.1); their labels come
// from the payload. The database group's options need glue with the picked
// server, an option of a server group. A Full stack option replaces the groups
// the Full stack group conflicts with (Frontend and Backend).
const DATABASE_GROUP = 'database';
const BACKEND_GROUP = 'backend';
const FULLSTACK_GROUP = 'fullstack';
const SERVER_GROUPS = [BACKEND_GROUP, FULLSTACK_GROUP];

/** "a", "a and b", "a, b and c". */
const joinWords = (words: string[]): string =>
  words.length <= 1 ? words.join('') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;

export const groupOf = (picker: CustomStackPicker, groupId: string): StackPickerGroup | undefined =>
  picker.groups.find((group) => group.id === groupId);

export const optionOf = (group: StackPickerGroup | undefined, optionId: string | undefined): StackPickerOption | undefined =>
  optionId ? group?.options.find((option) => option.id === optionId) : undefined;

/** The option picked in a group, if any. */
export const pickedOption = (picker: CustomStackPicker, picks: StackPicks, groupId: string): StackPickerOption | undefined =>
  optionOf(groupOf(picker, groupId), picks[groupId]);

/** The picks of options the picker lists: picks made before the picker was read
 *  again, less any group or option it no longer has. */
export function listedPicks(picker: CustomStackPicker, picks: StackPicks): StackPicks {
  const listed: StackPicks = {};
  for (const group of picker.groups) {
    if (optionOf(group, picks[group.id])) listed[group.id] = picks[group.id];
  }
  return listed;
}

/** Whether two picks choose the same option in every group. */
export function samePicks(a: StackPicks, b: StackPicks): boolean {
  const groups = (picks: StackPicks) => Object.keys(picks).filter((group) => picks[group]).sort();
  const aGroups = groups(a);
  const bGroups = groups(b);
  return aGroups.length === bGroups.length && aGroups.every((group, index) => group === bGroups[index] && a[group] === b[group]);
}

/** The architecture groups that follow groupId: those it is the parent of. */
const architecturesOf = (picker: CustomStackPicker, groupId: string): StackPickerGroup[] =>
  picker.groups.filter((group) => group.parent === groupId);

/** What a label shows for an architecture group after a parent picked without
 *  one of its options (stacks/parts.py default_label_of): its default label, such
 *  as "Unified", while the payload lists an option of it, else nothing. */
const defaultLabelOf = (architecture: StackPickerGroup): string | null =>
  architecture.options.length > 0 ? architecture.default_label : null;

/**
 * The label the server gives a stack (stacks/parts.py label_of): the picked
 * options' labels in the picker's order, joined with " + ", each with its
 * architecture in brackets after it, the one picked or else its group's default
 * (defaultLabelOf), such as "React (Single app) + Node.js (Unified) +
 * PostgreSQL" (multi-stack step 4.7); "Custom" without picks.
 */
export function stackLabel(picker: CustomStackPicker, picks: StackPicks): string {
  const words: string[] = [];
  for (const group of picker.groups) {
    if (group.parent) continue;
    const option = optionOf(group, picks[group.id]);
    if (!option) continue;
    const architectures = architecturesOf(picker, group.id)
      .map((architecture) => optionOf(architecture, picks[architecture.id])?.label ?? defaultLabelOf(architecture))
      .filter((label): label is string => !!label);
    words.push(architectures.length ? `${option.label} (${architectures.join(', ')})` : option.label);
  }
  return words.join(' + ') || NO_PICKS_LABEL;
}

/** picks without the given groups and the architectures that follow them. */
export function withoutGroups(picker: CustomStackPicker, picks: StackPicks, groupIds: string[]): StackPicks {
  const dropped = new Set(groupIds);
  for (const groupId of groupIds) architecturesOf(picker, groupId).forEach((group) => dropped.add(group.id));
  const kept: StackPicks = {};
  for (const [group, option] of Object.entries(picks)) {
    if (!dropped.has(group)) kept[group] = option;
  }
  return kept;
}

/** The first option of group, which picks nothing: its default label, such as an
 *  architecture's "Single app", else "None". */
export const noneLabel = (group: StackPickerGroup): string => group.default_label ?? 'None';

/** The groups picked now that conflict with group: for the Full stack group, the
 *  Frontend and Backend choices its Switch replaces; for Frontend or Backend, the
 *  Full stack choice that keeps their options off (replacedBy). */
export const blockingGroups = (picks: StackPicks, group: StackPickerGroup): string[] =>
  group.conflicts_with.filter((groupId) => picks[groupId]);

/** The groups a Full stack group replaces, in the payload's order: those it conflicts with. */
const replacedGroups = (picker: CustomStackPicker, fullstack: StackPickerGroup): StackPickerGroup[] =>
  fullstack.conflicts_with
    .map((groupId) => groupOf(picker, groupId))
    .filter((replaced): replaced is StackPickerGroup => !!replaced);

/** What a Full stack option builds together, in the payload's labels: "the frontend and the backend". */
const builtTogether = (replaced: StackPickerGroup[]): string =>
  joinWords(replaced.map((replacedGroup) => `the ${replacedGroup.label.toLowerCase()}`));

/**
 * The warning for picking option of the Full stack group while picks hold a
 * choice in a group it replaces, in the payload's words: it builds them
 * together, so it replaces the choices made in them, "your Frontend choice" or
 * "your Frontend and Backend choices" (T2 of the owner's review, 2026-10-20).
 * The pick applies only through Switch, which clears them (withoutGroups).
 * null for an option of any other group, which is never held for Switch
 * (optionState): a Frontend or a Backend never replaces a Full stack choice.
 */
export function conflictMessage(
  picker: CustomStackPicker,
  picks: StackPicks,
  group: StackPickerGroup,
  option: StackPickerOption,
): string | null {
  if (group.id !== FULLSTACK_GROUP) return null;
  const replaced = replacedGroups(picker, group);
  const chosen = replaced.filter((replacedGroup) => picks[replacedGroup.id]).map((replacedGroup) => replacedGroup.label);
  const named = chosen.length > 0 ? chosen : replaced.map((replacedGroup) => replacedGroup.label);
  return `${option.label} builds ${builtTogether(replaced)} together, so it replaces your ${joinWords(named)} ${named.length === 1 ? 'choice' : 'choices'}.`;
}

/** A picked Full stack option and its group. */
export interface FullStackPick {
  group: StackPickerGroup;
  option: StackPickerOption;
}

/**
 * The picked Full stack option while group is one it replaces (Frontend or
 * Backend), or undefined. Then group's options are off: the Full stack option
 * builds the frontend and the backend itself, and the user sets Full stack to
 * None before choosing one (owner, 2026-10-20). The create API refuses such a
 * pick ("A Full stack option replaces Frontend and Backend: choose one or the
 * other.").
 */
export function replacedBy(picker: CustomStackPicker, picks: StackPicks, group: StackPickerGroup): FullStackPick | undefined {
  if (!group.conflicts_with.includes(FULLSTACK_GROUP)) return undefined;
  const fullstack = groupOf(picker, FULLSTACK_GROUP);
  const option = optionOf(fullstack, picks[FULLSTACK_GROUP]);
  return fullstack && option ? { group: fullstack, option } : undefined;
}

/** Why group's options are off while by, a Full stack option, is picked, and how
 *  to choose one, in the payload's words: "<option> builds the frontend and the
 *  backend together. To choose a <group>, set <Full stack> to None." */
export const replacedNote = (picker: CustomStackPicker, group: StackPickerGroup, by: FullStackPick): string =>
  `${by.option.label} builds ${builtTogether(replacedGroups(picker, by.group))} together. `
  + `To choose a ${group.label}, set ${by.group.label} to ${noneLabel(by.group)}.`;

/** The picked server option: a Backend's or a Full stack's. */
export function serverOption(picker: CustomStackPicker, picks: StackPicks): StackPickerOption | undefined {
  for (const groupId of SERVER_GROUPS) {
    const option = pickedOption(picker, picks, groupId);
    if (option) return option;
  }
  return undefined;
}

/**
 * Why option of group can't be picked with picks, or null: this server can't run
 * it (the payload's reason), or it is a database without glue for the picked
 * server ("Not available with <server> yet").
 */
export function unavailableReason(
  picker: CustomStackPicker,
  picks: StackPicks,
  group: StackPickerGroup,
  option: StackPickerOption,
): string | null {
  if (!option.available) return option.reason ?? '';
  if (group.id === DATABASE_GROUP) {
    const server = serverOption(picker, picks);
    if (server && !(picker.pairs[server.id] ?? []).includes(option.id)) return `Not available with ${server.label} yet`;
  }
  return null;
}

/** Whether a picked option can't be picked with the others (unavailableReason). */
export function hasUnavailablePick(picker: CustomStackPicker, picks: StackPicks): boolean {
  return picker.groups.some((group) => {
    const option = optionOf(group, picks[group.id]);
    return !!option && unavailableReason(picker, picks, group, option) !== null;
  });
}

/** What the Custom option's groups do with an option (optionState). */
export type OptionState = 'off' | 'held' | 'applies';

/**
 * What the Custom option's groups do with option of group while picks are
 * picked, the one place its radio and a click on it are decided: 'off' while a
 * picked Full stack option replaces group (replacedBy) or option can't be
 * picked with picks (unavailableReason), when its radio is disabled and a click
 * does nothing; 'held' for a Full stack option while picks hold a choice it
 * replaces (blockingGroups), when a click shows conflictMessage's warning and
 * the pick applies only through its Switch; else 'applies', when a click picks
 * it. So the drawer never makes a pick the create API refuses as a Full stack
 * option beside a Frontend or a Backend.
 */
export function optionState(
  picker: CustomStackPicker,
  picks: StackPicks,
  group: StackPickerGroup,
  option: StackPickerOption,
): OptionState {
  if (replacedBy(picker, picks, group) || unavailableReason(picker, picks, group, option) !== null) return 'off';
  return blockingGroups(picks, group).length > 0 ? 'held' : 'applies';
}

/** A picked option whose needed group is empty, such as a Full stack option that needs a Database with none picked. */
export interface UnmetNeed {
  option: StackPickerOption;
  group: StackPickerGroup;
}

export function unmetNeeds(picker: CustomStackPicker, picks: StackPicks): UnmetNeed[] {
  const unmet: UnmetNeed[] = [];
  for (const group of picker.groups) {
    const option = optionOf(group, picks[group.id]);
    for (const needed of option?.needs ?? []) {
      const neededGroup = groupOf(picker, needed);
      if (option && neededGroup && !picks[needed]) unmet.push({ option, group: neededGroup });
    }
  }
  return unmet;
}

/** "<option label> needs a <group label>.", as the create API says it. */
export const needsMessage = ({ option, group }: UnmetNeed): string => `${option.label} needs a ${group.label}.`;

/** The warning a database picked without a server gets: it is allowed. */
export const noServerWarning = (backend: StackPickerGroup): string =>
  `No ${backend.label} is chosen, so the AI Model will write the code that uses this database, in what your special instructions name or what it chooses.`;

/** The hint a stack without a database gets. */
export const noDatabaseHint = (database: StackPickerGroup): string =>
  `Choose a ${database.label} to get the Database Viewer and data snapshots.`;

// A character Python's str.strip() takes off, which the platform trims custom
// instructions by: JavaScript's trim() takes \ufeff too, and not \x1c-\x1f or \x85.
const EDGE_SPACE = /[\t\n\v\f\r\x1c-\x20\x85\xa0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]/;

/** text without those characters at either end, found by stepping in from each
 *  end, in time in proportion to the text. A pattern that ends in a run of them
 *  and $ is tried again from every place in a run inside the text, in time that
 *  grows with the square of the run: a pasted text with a long run of blank
 *  lines would make every keystroke wait. */
const stripEdges = (text: string): string => {
  let start = 0;
  let end = text.length;
  while (start < end && EDGE_SPACE.test(text[start])) start += 1;
  while (end > start && EDGE_SPACE.test(text[end - 1])) end -= 1;
  return text.slice(start, end);
};

/** Custom instructions as the platform keeps them (backend/stacks/instructions.py):
 *  Windows line ends made "\n", then trimmed; "" means none. */
export const normaliseInstructions = (text: string | undefined): string =>
  stripEdges((text ?? '').replace(/\r\n/g, '\n'));

/** Their length as the platform counts it: characters, not UTF-16 units. */
export const instructionsLength = (text: string | undefined): number => Array.from(normaliseInstructions(text)).length;

const formatCount = (count: number): string => count.toLocaleString('en-US');

/** Their counter, such as "1,234 / 10,000". */
export const instructionsCounter = (text: string | undefined, maxChars: number): string =>
  `${formatCount(instructionsLength(text))} / ${formatCount(maxChars)}`;

/** What the platform says of instructions over the cap, or null within it. */
export const instructionsTooLong = (text: string | undefined, maxChars: number): string | null =>
  instructionsLength(text) > maxChars ? `Special instructions can be up to ${formatCount(maxChars)} characters.` : null;

/** What the platform says of emptying the custom instructions of a project they
 *  are the whole tech stack of (backend/stacks/instructions.py). */
export const WHOLE_STACK_MESSAGE = "Special instructions are this project's whole tech stack, so they can't be empty.";

/** Whether a project's custom instructions are its whole tech stack, which the
 *  platform refuses to empty: its picks, as the project's JSON sends them, are
 *  none. A project without a stack sends the picks of the parts it runs on, and
 *  one whose stack can't be read sends null, which the platform answers itself. */
export const instructionsAreWholeStack = (picks: StackPicks | null | undefined): boolean =>
  !!picks && Object.keys(picks).length === 0;

/** What the Edit project drawer knows of the project it edits, as the project's
 *  JSON sends it (GET /projects/). */
export interface EditedProject {
  /** 1 for a Web project, 2 for a Low Code one. */
  projectType?: number;
  /** As stored: null for a project older than prompt versions, which reads as 1. */
  promptVersion?: number | null;
  /** The label of its tech stack: null for a Low Code project and a stack that can't be read. */
  stackLabel?: string | null;
}

/**
 * Whether the Edit project drawer shows the project's tech stack and its Special
 * Instructions box (multi-stack step 3.14i, E1 of the owner's pick, 2026-10-20):
 * for a Web project on prompt version 2 or later, as its Custom Domain is, while
 * the Custom option is offered (picker), and only when its stack can be read, so
 * it has a label. Those are the projects whose special instructions the platform
 * changes (PUT /projects/{id}/custom-instructions).
 */
export const editShowsInstructions = (project: EditedProject, picker: CustomStackPicker | null): boolean =>
  project.projectType === 1 && (project.promptVersion ?? 0) >= 2 && !!picker && !!project.stackLabel;

/** What Save Changes in the Edit project drawer does with the text in its Special
 *  Instructions box (editedInstructions). */
export interface EditedInstructions {
  /** Whether it saves the text, before the project's other fields: the text
   *  differs from the one read once both are as the platform keeps them, so a
   *  text left unchanged after trimming sends nothing. */
  changed: boolean;
  /** Why Save Changes is off, in the platform's words, or null: the text is over
   *  the cap, or it is empty on a project made from special instructions alone,
   *  whose whole tech stack they are (T1), changed or not. */
  refusal: string | null;
}

/**
 * The Edit project drawer's rule for its Special Instructions box (multi-stack
 * step 3.14i), from the project's picks as its JSON sends them, the text read
 * (GET /projects/{id}/custom-instructions), the text in the box and the cap: the
 * refusals the platform would make of the text (stacks/instructions.py,
 * services/custom_instructions.py), which keep Save Changes off, and whether
 * the text changed, which alone sends it.
 */
export function editedInstructions(
  picks: StackPicks | null | undefined,
  saved: string,
  text: string,
  maxChars: number,
): EditedInstructions {
  const kept = normaliseInstructions(text);
  const emptied = kept === '' && instructionsAreWholeStack(picks);
  return {
    changed: kept !== normaliseInstructions(saved),
    refusal: instructionsTooLong(text, maxChars) ?? (emptied ? WHOLE_STACK_MESSAGE : null),
  };
}

/** The Tech Stack chosen in the Create Project drawer (multi-stack step 3.14g),
 *  or in the Clone drawer (step 3.14h). */
export interface StackChoice {
  /** The Tech Stack list's value: a preset's id, CUSTOM_STACK for the Custom
   *  option, or in the Clone drawer SOURCE_STACK for "Same as the source project". */
  stack?: string;
  /** The Custom option's picks: {}, None in every group, the first time it is
   *  chosen, then what is picked in it, kept while a preset is chosen after it. */
  stackPicks?: StackPicks;
  /** The Special Instructions box's text, which goes with any choice. */
  customInstructions?: string;
}

/**
 * choice once the Tech Stack list's value is set to value, a preset's id or
 * CUSTOM_STACK. The Custom option starts on None in every group the first time
 * it is chosen (owner, 2026-10-20), then keeps what is picked in it while the
 * drawer stays open, a preset chosen in between included; the box keeps its
 * text whatever is chosen.
 */
export function chooseStack<T extends StackChoice>(choice: T, value: string): T {
  return value === CUSTOM_STACK
    ? { ...choice, stack: value, stackPicks: choice.stackPicks ?? {} }
    : { ...choice, stack: value };
}

/** The preset a list preselects: the default, else its first. */
const defaultPresetId = (presets: StackPreset[]): string | undefined =>
  (presets.find((preset) => preset.default) ?? presets[0])?.id;

/**
 * choice as a Tech Stack list offering the values offered allows it, or choice
 * itself where it allows it as it is: a value it doesn't offer gives way to
 * preselected; without the Custom option its picks and the box's text go, since
 * the create API refuses both then; and the Custom option's picks lose any
 * option the list no longer has.
 */
function listedAmong<T extends StackChoice>(
  offered: string[],
  preselected: string | undefined,
  custom: CustomStackPicker | null,
  choice: T,
): T {
  let listed = choice;
  if (!custom) {
    if (listed.stackPicks !== undefined || listed.customInstructions) {
      listed = { ...listed, stackPicks: undefined, customInstructions: undefined };
    }
  } else if (listed.stackPicks) {
    const kept = listedPicks(custom, listed.stackPicks);
    if (!samePicks(kept, listed.stackPicks)) listed = { ...listed, stackPicks: kept };
  }
  if (!(listed.stack !== undefined && offered.includes(listed.stack)) && preselected !== listed.stack) {
    listed = { ...listed, stack: preselected };
  }
  return listed;
}

/** The values the Create Project drawer's Tech Stack list offers: each preset's id, then the Custom option's. */
const createChoices = (presets: StackPreset[], custom: CustomStackPicker | null): string[] => [
  ...presets.map((preset) => preset.id),
  ...(custom ? [CUSTOM_STACK] : []),
];

/**
 * choice as the Tech Stack list last read allows it, or choice itself where the
 * list allows it as it is: a preset the list no longer offers, or the Custom
 * option while it isn't offered, gives way to the default preset; without the
 * Custom option its picks and the box's text go, since the create API refuses
 * both then; and the Custom option's picks lose any option the list no longer has.
 */
export function listedChoice<T extends StackChoice>(
  presets: StackPreset[],
  custom: CustomStackPicker | null,
  choice: T,
): T {
  return listedAmong(createChoices(presets, custom), defaultPresetId(presets), custom, choice);
}

/** Whether something is chosen in the Custom option: a pick, or the box's text. */
const somethingChosen = (choice: StackChoice): boolean =>
  Object.keys(choice.stackPicks ?? {}).length > 0 || normaliseInstructions(choice.customInstructions) !== '';

/**
 * Whether a click on the backdrop beside the Create Project or Clone drawer
 * closes it, which drops what it holds, judged by what the drawer would send
 * (multi-stack step 3.14j): fields, the tech stack fields of its choice
 * (createStackFields, or cloneStackFields in the Clone drawer), which its request
 * carries only for a Web project, projectType 1 (components/Dashboard.tsx). Not
 * while they carry a pick of the Custom option or special instructions: only
 * Cancel and its close button discard them, as the Custom tech stack window they
 * came from did, and as the Edit project drawer keeps a change in its box. What
 * the drawer keeps but wouldn't send doesn't keep it open: the box's text, typed
 * or prefilled, once "Same as the source project", which hides the box, or Low
 * Code is chosen, and Custom's picks while another choice is made. picker is the
 * Custom option's, null while it isn't offered, when the drawer closes on that
 * click as before.
 */
export const closesOnBackdrop = (
  picker: CustomStackPicker | null,
  projectType: number | undefined,
  fields: CreateStackFields,
): boolean =>
  !picker
  || projectType !== 1
  || (Object.keys(fields.stack_picks ?? {}).length === 0 && normaliseInstructions(fields.custom_instructions) === '');

/** The width of the Create Project and Clone drawers, in pixels (drawerWidth). */
export type DrawerWidth = 400 | 720;

/**
 * The Create Project or Clone drawer's width (layout A of the owner's pick,
 * 2026-10-20; multi-stack steps 3.14g and 3.14l), worked out at each render: the
 * dashboard keeps the drawer mounted however often it opens and closes. decided
 * is the width the drawer holds from its last render (null as it mounts), stacks
 * the tech stacks as last read (null until they are), isOpen whether the
 * dashboard has it open, and isClosing whether it slides out after Cancel, its
 * close button or a click beside it. While it is open it keeps decided, else is
 * 720, capped at the screen, where stacks offer the Custom option, else 400. So
 * it is decided as the drawer opens where the dashboard's copy of the list is
 * read by then, else when the first list lands while it is open, its Tech Stack
 * list showing with it, and kept until it closes: a list read again never
 * resizes it. While it slides out it keeps the width it has, decided or not,
 * whatever list lands then, also once the dashboard closes it then, on a create
 * or a clone sent before a click beside it; closed, it has none, so the next
 * opening decides again. null while undecided, when the drawer is 400px wide and
 * shows no Tech Stack list, and while it is closed.
 */
export function drawerWidth(
  decided: DrawerWidth | null,
  stacks: StackCatalogue | null,
  isOpen: boolean,
  isClosing: boolean,
): DrawerWidth | null {
  if (isClosing) return decided;
  if (!isOpen) return null;
  return decided ?? (stacks ? (stacks.custom ? 720 : 400) : null);
}

/**
 * Whether Create Project can send choice's tech stack fields (createStackFields):
 * the create API would take them. The box's text is within the cap whatever is
 * chosen; with the Custom option, something is picked or written, every picked
 * option's needed group is picked, and each picked option can be picked with
 * the others.
 */
export function canCreateStack(picker: CustomStackPicker, choice: StackChoice): boolean {
  if (instructionsTooLong(choice.customInstructions, picker.instructions_max_chars)) return false;
  if (choice.stack !== CUSTOM_STACK) return true;
  const picks = choice.stackPicks ?? {};
  return somethingChosen(choice) && unmetNeeds(picker, picks).length === 0 && !hasUnavailablePick(picker, picks);
}

/** What the drawer's footer says beside Cancel (stackSummary). */
export interface StackSummary {
  text: string;
  /** In the quiet tone: why Create Project (or Clone Project) is off rather than a stack's name. */
  quiet: boolean;
}

/**
 * The footer's words for choice while the Custom option is chosen, so they stay
 * in view while the drawer scrolls: the label the stack will get, as the server
 * composes it, "Custom" for special instructions alone, or, with nothing picked
 * and nothing written, why it can't be created yet, as the create API says it;
 * null for a preset, or the Clone drawer's "Same as the source project", which
 * its row names.
 */
export function stackSummary(picker: CustomStackPicker, choice: StackChoice): StackSummary | null {
  if (choice.stack !== CUSTOM_STACK) return null;
  return somethingChosen(choice)
    ? { text: stackLabel(picker, choice.stackPicks ?? {}), quiet: false }
    : { text: NOTHING_CHOSEN_MESSAGE, quiet: true };
}

/** The tech stack fields of a create request (DESIGN_PARTS 11.2). */
export interface CreateStackFields {
  stack?: string;
  stack_picks?: StackPicks;
  custom_instructions?: string;
}

/**
 * The tech stack fields a Web project's create request sends for choice: stack
 * for a preset, or stack_picks for the Custom option ({} with nothing picked),
 * never both, which the API refuses; with either, custom_instructions, the box's
 * text, when it isn't empty once trimmed. The Custom option's picks, kept while
 * a preset is chosen after it, go only with the Custom option. Nothing without
 * a choice, when the server gives the default.
 */
export function createStackFields(choice: StackChoice): CreateStackFields {
  const instructions = normaliseInstructions(choice.customInstructions) !== ''
    ? { custom_instructions: choice.customInstructions }
    : {};
  if (choice.stack === CUSTOM_STACK) return { stack_picks: choice.stackPicks ?? {}, ...instructions };
  return choice.stack ? { stack: choice.stack, ...instructions } : {};
}

/** The Clone drawer's Tech Stack list value for "Same as the source project"
 *  (multi-stack step 3.14h): outside the grammar of a preset's id, lowercase
 *  words joined by hyphens (backend/stacks/parts.py), so no preset takes it and
 *  a list read again never gives it way to the default. */
export const SOURCE_STACK = '@source';

/** What the Clone drawer knows of the project it clones, as the project's JSON
 *  sends it (GET /projects/). */
export interface CloneSource {
  /** 1 for a Web project, 2 for a Low Code one. */
  projectType?: number;
  /** As stored: null for a project older than prompt versions, which reads as 1. */
  promptVersion?: number | null;
  /** The label of its tech stack (stack_label), which "Same as the source
   *  project" shows: null for a Low Code project and for a stack that can't be
   *  read. */
  label?: string | null;
  /** The picks of the parts it is built on (stack_picks, group -> part id): null
   *  for a Low Code project and for a stack that can't be read. */
  picks?: StackPicks | null;
  /** Whether it has special instructions (has_custom_instructions). */
  hasCustomInstructions?: boolean;
}

/** The prompt version whose turns are composed from a stack's parts
 *  (backend/stacks/profile.py COMPOSED_PROMPT_VERSION); version 1 keeps its frozen
 *  text, so code written on it was written for no picks. */
const COMPOSED_PROMPT_VERSION = 2;

const isWebSource = (source: CloneSource): boolean => (source.projectType ?? 1) === 1;

/** The presets the Clone drawer lists for source: the payload's, but for a Web
 *  source whose stack can be read the one on its own picks, which "Same as the
 *  source project" stands for. */
export function clonePresets(source: CloneSource, presets: StackPreset[]): StackPreset[] {
  const own = isWebSource(source) ? source.picks : null;
  return own ? presets.filter((preset) => !samePicks(preset.picks, own)) : presets;
}

/** Whether "Same as the source project" is a Web source's one choice while the
 *  Custom option isn't offered. Where the source has special instructions, it
 *  is the one choice that copies them: the box that carries them to another
 *  stack shows only with that option, and the clone API refuses them without it
 *  (backend/stacks/registry.py), so another stack would drop them unseen. Where
 *  its stack can't be read (no picks), the drawer can't tell which preset, if
 *  any, the source is on, so its clone keeps its stack, as every clone did
 *  before step 3.14h, and such a stack, without a label, shows nothing there,
 *  as then (cloneStackSection; multi-stack step 3.14j). */
const onlySameWithoutCustom = (source: CloneSource, custom: CustomStackPicker | null): boolean =>
  !custom && isWebSource(source) && (!!source.hasCustomInstructions || !source.picks);

/** The values the Clone drawer's Tech Stack list offers, in its order: "Same as
 *  the source project" for a Web source, the presets clonePresets lists, then
 *  the Custom option while it is offered; "Same as the source project" alone
 *  where onlySameWithoutCustom says so. With one only, the drawer shows it as
 *  text, or nothing (cloneStackSection). */
export const cloneChoices = (source: CloneSource, presets: StackPreset[], custom: CustomStackPicker | null): string[] =>
  onlySameWithoutCustom(source, custom) ? [SOURCE_STACK] : [
    ...(isWebSource(source) ? [SOURCE_STACK] : []),
    ...createChoices(clonePresets(source, presets), custom),
  ];

/** What the Clone drawer says above the label of its one choice shown as text:
 *  a Web source's "Same as the source project", a Low Code source's default. */
const SAME_AS_SOURCE_NOTE = 'Same as the source project.';
const DEFAULT_PRESET_NOTE = 'The default for Web projects.';

/** What the Clone drawer's Tech Stack section shows for a Web clone (cloneStackSection). */
export type CloneStackSection =
  | { kind: 'list' }
  | { kind: 'text'; note: string; label: string }
  | { kind: 'none' };

/**
 * What the Clone drawer's Tech Stack section shows for a Web clone of source,
 * from stacks, the tech stacks as last read, null until they are: the list
 * (TechStackField) while it offers more than one choice (cloneChoices), and for
 * a Low Code source until it is read, when the list says so; else its one
 * choice as text, as before step 3.14h, "Same as the source project." with a
 * Web source's label, only while its stack has one (multi-stack step 3.14j), or
 * "The default for Web projects." with that preset's label for a Low Code
 * source; else nothing, as for a Web source whose stack can't be read.
 */
export function cloneStackSection(source: CloneSource, stacks: StackCatalogue | null): CloneStackSection {
  const choices = stacks ? cloneChoices(source, stacks.presets, stacks.custom) : [];
  if (stacks ? choices.length > 1 : !isWebSource(source)) return { kind: 'list' };
  if (isWebSource(source)) {
    return source.label ? { kind: 'text', note: SAME_AS_SOURCE_NOTE, label: source.label } : { kind: 'none' };
  }
  const preset = stacks?.presets.find((candidate) => candidate.id === choices[0]);
  return preset ? { kind: 'text', note: DEFAULT_PRESET_NOTE, label: preset.label } : { kind: 'none' };
}

/**
 * The Clone drawer's choice as the Tech Stack list last read allows it, as
 * listedChoice does Create Project's: "Same as the source project" stays for a
 * Web source, whatever the list holds, and a value the list no longer offers
 * gives way to it, or for a Low Code source to the default preset.
 */
export function listedCloneChoice<T extends StackChoice>(
  source: CloneSource,
  presets: StackPreset[],
  custom: CustomStackPicker | null,
  choice: T,
): T {
  const preselected = isWebSource(source) ? SOURCE_STACK : defaultPresetId(presets);
  return listedAmong(cloneChoices(source, presets, custom), preselected, custom, choice);
}

/**
 * The tech stack fields a Web clone's request sends for choice: none for "Same
 * as the source project", when the clone keeps its source's stack, code and
 * special instructions, else what Create Project would send
 * (createStackFields), whose special instructions replace the source's.
 */
export const cloneStackFields = (choice: StackChoice): CreateStackFields =>
  choice.stack === SOURCE_STACK ? {} : createStackFields(choice);

/** choice with the Special Instructions box prefilled from the source's, text:
 *  once a choice other than "Same as the source project", which copies them as
 *  they are, shows the box, and only while it holds nothing yet; the text in it,
 *  "" included, is the user's from then on. */
export function withSourceInstructions<T extends StackChoice>(choice: T, text: string): T {
  if (!choice.stack || choice.stack === SOURCE_STACK || choice.customInstructions !== undefined) return choice;
  return { ...choice, customInstructions: text };
}

/** Whether Clone Project can send choice's tech stack fields (cloneStackFields):
 *  always for "Same as the source project", which sends none, else where Create
 *  Project could (canCreateStack). */
export const canCloneStack = (picker: CustomStackPicker, choice: StackChoice): boolean =>
  choice.stack === SOURCE_STACK || canCreateStack(picker, choice);

/**
 * Whether a Web clone on choice gets its source's code, as the clone API decides
 * it from the fields cloneStackFields sends (backend/routers/projects.py
 * _clone_stack_choice, services/clone_service.py clone_stack_of): a request that
 * names no stack, "Same as the source project", keeps a Web source's stack as
 * stored, with its code, and a Low Code source's clone gets the default without
 * it; one that names a stack keeps the code only for a Web source on prompt
 * version 2 whose stack can be read and has the same option in every group as
 * the choice, a preset's picks or Custom's (samePicks). Special instructions
 * never decide it. presets are the payload's, from which a preset's picks are read.
 */
export function keepsSourceCode(source: CloneSource, choice: StackChoice, presets: StackPreset[]): boolean {
  const fields = cloneStackFields(choice);
  if (fields.stack === undefined && fields.stack_picks === undefined) return isWebSource(source);
  if (!isWebSource(source) || source.promptVersion !== COMPOSED_PROMPT_VERSION || !source.picks) return false;
  const picks = fields.stack_picks ?? presets.find((preset) => preset.id === fields.stack)?.picks;
  return !!picks && samePicks(source.picks, picks);
}

/** The Clone drawer's line under its list, saying whether the clone gets the
 *  source code (keepsSourceCode). */
export const sourceCodeLine = (keeps: boolean): string =>
  keeps ? 'The source code is copied.' : "The source code isn't copied, since the tech stack changes.";

/** The database group, for its warning and hint, while the picker has one. */
export const databaseGroup = (picker: CustomStackPicker): StackPickerGroup | undefined => groupOf(picker, DATABASE_GROUP);

/** The Backend group, whose label the database warning names. */
export const backendGroup = (picker: CustomStackPicker): StackPickerGroup | undefined => groupOf(picker, BACKEND_GROUP);
